/**
 * Pure pixel + colour helpers for the mask editor.
 *
 * Extracted from `MaskEditor.tsx`'s tail (WS-J / AUD-2 RC-4) with no change to a single body.
 * They are the only part of that file with no React and no closure over component state, which
 * is why they moved first: nothing downstream can observe the move. They now have direct unit
 * tests (`pixels.test.ts`) — before this they were reachable only by mounting a 4,449-line
 * component, which is the size problem in miniature.
 */
import type { Polygon } from "@genz/engine-bind";

/**
 * Get a 2D canvas context, or null when unavailable. jsdom's `getContext`
 * THROWS ("Not implemented") rather than returning null, so we swallow it —
 * this is the feature-detect guard that keeps the chroma preview a no-op in
 * test environments without a real canvas.
 */
export function get2dContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext?.("2d") ?? null;
  } catch {
    return null;
  }
}

/** "#rrggbb" → [r,g,b], or null when it isn't a 6-hex colour. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** [r,g,b] → "#rrggbb". */
export function rgbToHexColor(c: [number, number, number]): string {
  return "#" + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
}

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Ray-cast point-in-polygon for normalized (0..1) coords. Used by "protect product"
 *  to keep the print-area region during auto background removal. */
export function pointInPolyNorm(x: number, y: number, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-9) + xi) inside = !inside;
  }
  return inside;
}

/** Encode an RGBA buffer to PNG bytes via an offscreen canvas, or null when canvas
 *  isn't available (jsdom/no-DOM) — the layered-mockup preview feature-detects on this. */
/** A solid white, fully-opaque PNG at `w×h`. UV varnish is generated from the DESIGN and needs
 *  no texture image — only full print-area coverage — so this stands in when no texture is
 *  uploaded (its pixels only mark coverage; the print clip confines the effect). */
export function solidOpaqueBytes(w: number, h: number): Uint8Array | null {
  const rgba = new Uint8ClampedArray(w * h * 4);
  rgba.fill(255);
  return rgbaToPngBytes(rgba, w, h);
}

export function rgbaToPngBytes(rgba: Uint8ClampedArray, w: number, h: number): Uint8Array | null {
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = get2dContext(c);
  if (!ctx || typeof ctx.putImageData !== "function" || typeof c.toDataURL !== "function") return null;
  try {
    const img = ctx.createImageData(w, h);
    img.data.set(rgba);
    ctx.putImageData(img, 0, 0);
    return dataUrlToBytes(c.toDataURL("image/png"));
  } catch {
    return null;
  }
}

/** Decode a `data:image/png;base64,…` URL to raw bytes. */
export function dataUrlToBytes(dataUrl: string): Uint8Array {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Decode a grayscale mask PNG (from the native segmenter) into a per-pixel alpha
 *  array (R channel) sized w×h. Returns null if canvas/Image aren't available. */
export async function decodeMaskAlpha(pngBytes: Uint8Array, w: number, h: number): Promise<Uint8Array | null> {
  if (typeof document === "undefined" || typeof Image === "undefined" || typeof URL === "undefined") return null;
  const blob = new Blob([pngBytes as BlobPart], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = url;
    });
    if (!img) return null;
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = get2dContext(c);
    if (!ctx || typeof ctx.getImageData !== "function") return null;
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const alpha = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) alpha[i] = data[i * 4]; // R = mask luma
    return alpha;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Decode a PNG into its raw RGBA bytes sized w×h (via an offscreen canvas), or null
 *  when canvas/Image aren't available. Used to read the magenta keyer's despilled product
 *  so the shell can be rebuilt with the right alpha. */
export async function decodePngToRgba(pngBytes: Uint8Array, w: number, h: number): Promise<Uint8ClampedArray | null> {
  if (typeof document === "undefined" || typeof Image === "undefined" || typeof URL === "undefined") return null;
  const blob = new Blob([pngBytes as BlobPart], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = url;
    });
    if (!img) return null;
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = get2dContext(c);
    if (!ctx || typeof ctx.getImageData !== "function") return null;
    ctx.drawImage(img, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h).data;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Decoded pixels of the image at `url`. */
export function loadImagePixels(url: string): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("no 2d canvas"));
      ctx.drawImage(img, 0, 0);
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
      resolve({ data: d.data, width: d.width, height: d.height });
    };
    img.onerror = () => reject(new Error("The image did not load."));
    img.src = url;
  });
}

